import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { useSession } from "@/app/session";
import { QuickNotesPanel } from "@/board/QuickNotesPanel";
import { FeedbackButton } from "@/components/FeedbackDialog";
import { useT } from "@/lib/i18n";
import { LanguageToggle } from "./LanguageToggle";
import { SearchButton, SearchDialog, useSearchHotkey } from "./SearchDialog";
import { Wordmark } from "./ui";

export function Shell({
  children,
  crumbs,
}: {
  children: ReactNode;
  crumbs?: { id: string; title: string }[];
}) {
  const t = useT();
  const { me, signOut } = useSession();
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  useSearchHotkey(setSearchOpen);
  return (
    <div className="flex min-h-full flex-col">
      {searchOpen ? <SearchDialog onClose={() => setSearchOpen(false)} /> : null}
      <header className="flex items-center gap-4 border-b border-border bg-panel px-4 py-3">
        <Wordmark className="shrink-0 text-xl" />
        {crumbs?.length ? (
          <nav className="flex min-w-0 items-center gap-1 overflow-hidden text-sm text-muted">
            {crumbs.map((c, i) => (
              <span key={c.id} className="flex min-w-0 items-center gap-1">
                {i > 0 ? <span className="text-dim">›</span> : null}
                {i === crumbs.length - 1 ? (
                  <span className="truncate font-semibold text-text">{c.title}</span>
                ) : (
                  <Link to={`/b/${c.id}`} className="truncate hover:text-text">
                    {c.title}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        ) : null}
        <div className="ml-auto flex items-center gap-3 text-sm">
          {me ? <SearchButton /> : null}
          {me ? (
            <button
              type="button"
              onClick={() => setQuickOpen((v) => !v)}
              className="rounded-sm border border-border px-2 py-1 text-xs text-muted hover:text-text"
              title={t.quickNotes}
            >
              ✎ {t.quickNotes}
            </button>
          ) : null}
          {me?.staffRole ? (
            <>
              <Link to="/equipo" className="text-muted hover:text-text">
                {t.team}
              </Link>
              <Link to="/papelera" className="text-muted hover:text-text">
                {t.trash}
              </Link>
            </>
          ) : null}
          {me ? (
            <Link to="/guia" className="text-muted hover:text-text">
              {t.guide}
            </Link>
          ) : null}
          {me ? <FeedbackButton /> : null}
          <LanguageToggle />
          {me ? (
            <Link to="/ajustes" className="hidden text-muted hover:text-text sm:inline" title={t.settings}>
              {me.user.name}
            </Link>
          ) : null}
          <button type="button" onClick={signOut} className="text-muted hover:text-text">
            {t.signOut}
          </button>
        </div>
      </header>
      <main className="relative flex-1">
        {children}
        {quickOpen ? <QuickNotesPanel target={null} onClose={() => setQuickOpen(false)} /> : null}
      </main>
    </div>
  );
}
